// WP-C1 — daily labour attendance inside Work Progress. Eligibility, units
// and summaries are SERVER-derived; this UI only submits status choices.
// One sheet per project+date; corrections are append-only revisions.
import { useCallback, useEffect, useState } from "react";
import { usePager, Paginator } from "@/components/Paginator";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, FileSpreadsheet, Eye, Pencil, CheckCircle2, XCircle, Paperclip, Lock, Search } from "lucide-react";
import { compressImage } from "@/lib/imageCompress";

const openSheetAttachment = async (projectId, sheetId) => {
  try {
    const r = await api.get(`/projects/${projectId}/labour-attendance/${sheetId}/attachment`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { toast.error(e?.response?.data?.detail || "Failed to open attachment"); }
};

const inputCls = "border border-slate-300 rounded px-2 py-1.5 text-xs w-full";
const today = () => new Date().toLocaleDateString("en-CA");
const STATUSES = ["Present", "Absent", "Half Day", "Weekly Off", "Holiday", "Leave"];
const UNITS = { Present: 1, "Half Day": 0.5, Absent: 0, "Weekly Off": 0, Holiday: 0, Leave: 0 };

const summarize = (list) => {
  const s = { total: list.length, Present: 0, Absent: 0, "Half Day": 0, "Weekly Off": 0, Holiday: 0, Leave: 0, effective: 0, overtime: 0, notset: 0 };
  const skills = {};
  list.forEach((r) => {
    const ot = r.status === "Present" ? (parseFloat(r.overtime) || 0) : 0;
    if (!r.status) s.notset += 1;
    else s[r.status] = (s[r.status] || 0) + 1;
    s.effective += (UNITS[r.status] ?? 0) + ot;
    s.overtime += ot;
    const k = r.skill_category || "—";
    skills[k] = skills[k] || { skill: k, assigned: 0, present: 0, half: 0, effective: 0 };
    skills[k].assigned += 1;
    if (r.status === "Present") skills[k].present += 1;
    if (r.status === "Half Day") skills[k].half += 1;
    skills[k].effective += (UNITS[r.status] ?? 0) + ot;
  });
  s.effective = Math.round(s.effective * 100) / 100;
  s.overtime = Math.round(s.overtime * 100) / 100;
  const sk = Object.values(skills).sort((a, b) => a.skill.localeCompare(b.skill));
  sk.forEach((x) => { x.effective = Math.round(x.effective * 100) / 100; });
  return { s, sk };
};

const chipDefs = [
  ["total", "Assigned"], ["notset", "Not Set"], ["Present", "Present"], ["Absent", "Absent"],
  ["Half Day", "Half Day"], ["Weekly Off", "Weekly Off"], ["Holiday", "Holiday"],
  ["Leave", "Leave"], ["overtime", "Overtime"], ["effective", "Effective"],
];

function SummaryStrip({ list }) {
  const { s } = summarize(list);
  return (
    <div className="flex items-center gap-2 flex-wrap text-[11px]" data-testid="att-summary-strip">
      {chipDefs.filter(([k]) => k !== "notset" || s.notset > 0).map(([k, label]) => (
        <span key={k} data-testid={`att-chip-${label.toLowerCase().replace(" ", "-")}`}
          className={`px-2 py-1 rounded border ${k === "effective" ? "bg-emerald-50 border-emerald-300 text-emerald-900 font-bold" : k === "notset" ? "bg-rose-50 border-rose-300 text-rose-800 font-bold" : "bg-slate-50 border-slate-200 text-slate-700"}`}>
          {label}: <b>{s[k] ?? 0}</b>
        </span>
      ))}
    </div>
  );
}

function SkillSummaryTable({ list }) {
  const { sk } = summarize(list);
  if (sk.length === 0) return null;
  return (
    <div className="border border-slate-200 rounded overflow-x-auto">
      <table className="w-full text-xs" data-testid="att-skill-summary-table">
        <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
          <tr><th className="px-3 py-1.5 text-left">Skill Category</th>
            <th className="px-3 py-1.5 text-right">Assigned</th>
            <th className="px-3 py-1.5 text-right">Present</th>
            <th className="px-3 py-1.5 text-right">Half Day</th>
            <th className="px-3 py-1.5 text-right">Effective Count</th></tr>
        </thead>
        <tbody>
          {sk.map((r) => (
            <tr key={r.skill} className="border-t border-slate-100" data-testid={`att-skill-row-${r.skill}`}>
              <td className="px-3 py-1.5">{r.skill}</td>
              <td className="px-3 py-1.5 text-right">{r.assigned}</td>
              <td className="px-3 py-1.5 text-right">{r.present}</td>
              <td className="px-3 py-1.5 text-right">{r.half}</td>
              <td className="px-3 py-1.5 text-right font-bold">{r.effective}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SheetDetailModal({ project, sheetId, onClose }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get(`/projects/${project.id}/labour-attendance/${sheetId}`)
      .then((r) => setS(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Failed to load sheet"); onClose(); });
  }, [project.id, sheetId, onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10" data-testid="att-detail-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="text-sm font-bold text-slate-900">Attendance — {s?.attendance_date || "…"} · Rev {s?.revision || ""}</div>
          <button data-testid="att-detail-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        {s && (
          <div className="p-5 space-y-3 text-xs">
            <SummaryStrip list={s.rows || []} />
            <div className="border border-slate-200 rounded overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                  <tr><th className="px-3 py-1.5 text-left">Labour ID</th><th className="px-3 py-1.5 text-left">Name</th>
                    <th className="px-3 py-1.5 text-left">Tag</th>
                    <th className="px-3 py-1.5 text-left">Skill</th><th className="px-3 py-1.5 text-left">Type</th>
                    <th className="px-3 py-1.5 text-left">Status</th><th className="px-3 py-1.5 text-right">OT</th><th className="px-3 py-1.5 text-right">Units</th>
                    <th className="px-3 py-1.5 text-left">Cat 1</th><th className="px-3 py-1.5 text-left">Cat 2</th>
                    <th className="px-3 py-1.5 text-left">Remark</th></tr>
                </thead>
                <tbody>
                  {(s.rows || []).map((r) => (
                    <tr key={r.labour_ref_id} className="border-t border-slate-100">
                      <td className="px-3 py-1.5 font-mono font-bold">{r.labour_code}</td>
                      <td className="px-3 py-1.5">{r.name}</td>
                      <td className="px-3 py-1.5">{r.tag ? <span className="inline-block bg-indigo-50 border border-indigo-200 text-indigo-800 rounded px-1.5 py-0.5 text-[10px] font-bold">{r.tag}</span> : "—"}</td>
                      <td className="px-3 py-1.5">{r.skill_category}</td>
                      <td className="px-3 py-1.5">{r.labour_type}</td>
                      <td className="px-3 py-1.5 font-bold">{r.status}</td>
                      <td className="px-3 py-1.5 text-right">{(r.overtime || 0) > 0 ? <span className="text-orange-700 font-bold">+{r.overtime}</span> : "—"}</td>
                      <td className="px-3 py-1.5 text-right">{r.units}</td>
                      <td className="px-3 py-1.5">{r.category1_name || "—"}</td>
                      <td className="px-3 py-1.5">{r.category2_name || "—"}</td>
                      <td className="px-3 py-1.5 max-w-[160px] truncate" title={r.remark}>{r.remark || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <SkillSummaryTable list={s.rows || []} />
            <div className="text-[10px] text-slate-500">
              Site remark: <i>{s.site_remark}</i> · marked by {s.created_by} on {(s.created_at || "").slice(0, 16).replace("T", " ")}
              {s.backdated_entry && <span className="ml-1 text-amber-700 font-bold">· backdated entry</span>}
              {s.attachment?.filename && (
                <button data-testid="att-detail-attachment" onClick={() => openSheetAttachment(project.id, s.id)}
                  className="ml-2 inline-flex items-center gap-1 text-sky-700 font-bold hover:underline">
                  <Paperclip size={10} /> {s.attachment.filename}</button>
              )}
            </div>
            {(s.revisions || []).length > 0 && (
              <div className="space-y-1" data-testid="att-revisions">
                <div className="text-[10px] uppercase font-bold text-slate-400">Correction history (append-only)</div>
                {s.revisions.map((rv) => (
                  <div key={rv.revision} className="border border-amber-200 bg-amber-50/60 rounded px-3 py-2" data-testid={`att-revision-${rv.revision}`}>
                    <div className="font-bold text-amber-900">Rev {rv.revision} — {rv.corrected_by} · {(rv.corrected_at || "").slice(0, 16).replace("T", " ")}</div>
                    <div className="text-slate-700">Reason: {rv.reason}</div>
                    {(rv.changes || []).map((c) => (
                      <div key={c.labour_ref_id} className="text-[11px] text-slate-600">
                        {c.labour_code}: <b>{c.before_status}</b> → <b>{c.after_status}</b>
                        {c.before_remark !== c.after_remark ? ` · remark "${c.before_remark}" → "${c.after_remark}"` : ""}
                      </div>
                    ))}
                    {rv.site_remark_after && <div className="text-[11px] text-slate-600">Site remark → "{rv.site_remark_after}"</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function LabourAttendanceTab({ project }) {
  const { hasPerm } = useAuth();
  const canCreate = hasPerm("labour_attendance", "create");
  const canEdit = hasPerm("labour_attendance", "edit");
  const [view, setView] = useState("daily");
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);           // {rows, sheet, can_backdate}
  const [marks, setMarks] = useState({});           // labour_ref_id -> {status, remark}
  const [siteRemark, setSiteRemark] = useState("");
  const [reason, setReason] = useState("");
  const [correcting, setCorrecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [attFile, setAttFile] = useState(null);
  const [cats, setCats] = useState({ cat1: [], cat2: [] });
  const [hist, setHist] = useState({ rows: [], total: 0 });
  const [range, setRange] = useState({ from: "", to: "" });
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState("");
  const [rates, setRates] = useState({});           // labour_ref_id -> per-day ₹ (labour_payments editors only)
  // Aug-2026 (user request) — search filter (name / tag / ID) + multi-tick
  // so a tag can be filtered and actions applied to the ticked rows only.
  const [q, setQ] = useState("");
  const [ticks, setTicks] = useState({});           // labour_ref_id -> true

  useEffect(() => {
    api.get("/grn-categories").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setCats({ cat1: list.filter((x) => x.kind === "cat1"), cat2: list.filter((x) => x.kind === "cat2") });
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${project.id}/labour-attendance/eligible`,
        { params: { attendance_date: date } });
      const d = r.data;
      setData(d); setErr(""); setCorrecting(false); setReason("");
      const src = d.sheet ? d.sheet.rows : d.rows;
      const m = {};
      src.forEach((x) => { m[x.labour_ref_id] = { status: x.status || "", remark: x.remark || "", overtime: x.overtime || 0, category1_id: x.category1_id || "", category2_id: x.category2_id || "" }; });
      setMarks(m);
      const rm = {};
      Object.entries(d.wages || {}).forEach(([k, w]) => { rm[k] = w.per_day_amount; });
      setRates(rm);
      setAttFile(null);
      setTicks({});
      setSiteRemark(d.sheet ? d.sheet.site_remark : "");
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to load attendance"); setData(null); }
  }, [project.id, date]);
  useEffect(() => { if (view === "daily") load(); }, [view, load]);

  const loadHist = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${project.id}/labour-attendance`, { params: {
        from_date: range.from || undefined, to_date: range.to || undefined, limit: 100 } });
      setHist(r.data || {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load history"); }
  }, [project.id, range]);
  useEffect(() => { if (view === "history") loadHist(); }, [view, loadHist]);

  const sheet = data?.sheet || null;
  const rows = sheet ? sheet.rows : (data?.rows || []);
  const editable = (!sheet && canCreate) || (sheet && correcting);
  const qt = q.trim().toLowerCase();
  const visible = !qt ? rows : rows.filter((r) =>
    [r.name, r.tag, r.labour_code].some((v) => String(v || "").toLowerCase().includes(qt)));
  const tickedRows = rows.filter((r) => ticks[r.labour_ref_id]);
  const rowPager = usePager(visible, 25);
  const histPager = usePager(hist?.rows, 10);
  const backdated = date < today();
  const marked = rows.map((r) => ({ ...r,
    status: marks[r.labour_ref_id]?.status || r.status || "",
    overtime: marks[r.labour_ref_id]?.overtime ?? r.overtime ?? 0 }));

  // Jun-2026 — per-day wage + live projected cost (labour_payments editors only)
  const canRate = !!(data?.can_view_rate || data?.can_edit_rate);
  const canEditRate = !!data?.can_edit_rate;
  const wages = data?.wages || {};
  const unitsOf = (r) => {
    const st = marks[r.labour_ref_id]?.status || "";
    const ot = st === "Present" ? (parseFloat(marks[r.labour_ref_id]?.overtime) || 0) : 0;
    return (UNITS[st] || 0) + ot;
  };
  const rateOf = (id) => parseFloat(rates[id]) || 0;
  const estOf = (r) => unitsOf(r) * rateOf(r.labour_ref_id);
  const projectedTotal = rows.reduce((s, r) => s + estOf(r), 0);
  const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

  const setMark = (id, patch) => setMarks((m) => ({ ...m, [id]: { ...m[id], ...patch } }));
  const applyStatus = (status, targets) => setMarks((m) => {
    const n = { ...m };
    targets.forEach((r) => {
      n[r.labour_ref_id] = { ...(n[r.labour_ref_id] || {}), status,
        ...(status !== "Present" ? { overtime: 0 } : {}) };
    });
    return n;
  });
  const toggleTickAll = () => {
    const allTicked = visible.length > 0 && visible.every((r) => ticks[r.labour_ref_id]);
    setTicks((t) => {
      const n = { ...t };
      visible.forEach((r) => { if (allTicked) delete n[r.labour_ref_id]; else n[r.labour_ref_id] = true; });
      return n;
    });
  };

  const save = async () => {
    if (!siteRemark.trim()) return toast.error("Overall site-attendance remark is mandatory");
    if (sheet && correcting && !reason.trim()) return toast.error("A correction reason is mandatory");
    const unmarked = rows.filter((r) => !(marks[r.labour_ref_id]?.status));
    if (unmarked.length) return toast.error(`Mark the attendance status of every labourer — ${unmarked.length} still "Not set" (e.g. ${unmarked[0].labour_code})`);
    if (!sheet && !attFile) return toast.error("Attach the site attendance photo/PDF — mandatory before saving");
    setBusy(true);
    try {
      let attachment = null;
      if (attFile) {
        let f = attFile;
        if (/^image\//.test(attFile.type || "")) { try { f = await compressImage(attFile); } catch { f = attFile; } }
        const fd = new FormData();
        fd.append("file", f, attFile.name);
        const up = await api.post(`/projects/${project.id}/labour-attendance/attachment`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        attachment = up.data;
      }
      const payload_rows = rows.map((r) => ({
        labour_ref_id: r.labour_ref_id,
        status: marks[r.labour_ref_id]?.status || "",
        overtime: parseFloat(marks[r.labour_ref_id]?.overtime) || 0,
        category1_id: marks[r.labour_ref_id]?.category1_id || "",
        category2_id: marks[r.labour_ref_id]?.category2_id || "",
        remark: marks[r.labour_ref_id]?.remark || "",
        ...(canEditRate && rates[r.labour_ref_id] !== undefined && rates[r.labour_ref_id] !== ""
          && parseFloat(rates[r.labour_ref_id]) !== (wages[r.labour_ref_id]?.per_day_amount ?? null)
          ? { per_day_amount: parseFloat(rates[r.labour_ref_id]) } : {}) }));
      if (sheet && correcting) {
        await api.put(`/projects/${project.id}/labour-attendance/${sheet.id}`,
          { reason: reason.trim(), rows: payload_rows, site_remark: siteRemark.trim(),
            ...(attachment ? { attachment } : {}) });
        toast.success("Attendance corrected (new revision recorded)");
      } else {
        await api.post(`/projects/${project.id}/labour-attendance`,
          { attendance_date: date, site_remark: siteRemark.trim(), rows: payload_rows, attachment });
        toast.success("Attendance saved");
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const doExport = async () => {
    try {
      const r = await api.get(`/projects/${project.id}/labour-attendance-export`, {
        responseType: "blob",
        params: { from_date: range.from || undefined, to_date: range.to || undefined } });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = `attendance_${project.project_code}.xlsx`; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed (attendance export permission required)"); }
  };

  return (
    <div className="space-y-3" data-testid="att-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1 border-b border-slate-200 text-xs">
          {[["daily", "Daily Sheet"], ["history", "History"]].map(([k, label]) => (
            <button key={k} data-testid={`att-view-${k}`} onClick={() => setView(k)}
              className={`px-3 py-1.5 font-bold border-b-2 -mb-px ${view === k ? "border-[#F97316] text-[#0B1F3A]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              {label}
            </button>
          ))}
        </div>
        <button data-testid="att-export-btn" onClick={doExport}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-emerald-800 border border-emerald-300 rounded hover:bg-emerald-50">
          <FileSpreadsheet size={13} /> Export Excel
        </button>
      </div>

      {view === "daily" && (
        <div className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <label className="flex items-center gap-2 font-bold text-slate-600">Attendance Date
              <input data-testid="att-date-input" type="date" max={today()} className="border border-slate-300 rounded px-2 py-1.5"
                value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
            {backdated && (
              <span className="text-amber-800 bg-amber-50 border border-amber-300 rounded px-2 py-1 text-[11px]" data-testid="att-backdate-note">
                Backdated date — requires the attendance backdating permission{data?.can_backdate === false ? " (you don't have it)" : ""}
              </span>
            )}
            {sheet && !correcting && (
              <span className="text-emerald-800 bg-emerald-50 border border-emerald-300 rounded px-2 py-1 text-[11px] font-bold" data-testid="att-saved-badge">
                Saved · Rev {sheet.revision}{sheet.backdated_entry ? " · backdated entry" : ""}
              </span>
            )}
            {sheet && sheet.attachment?.filename && (
              <button data-testid="att-saved-attachment" onClick={() => openSheetAttachment(project.id, sheet.id)}
                className="inline-flex items-center gap-1 text-sky-700 border border-sky-300 bg-sky-50 rounded px-2 py-1 text-[11px] font-bold hover:bg-sky-100">
                <Paperclip size={11} /> {sheet.attachment.filename}</button>
            )}
          </div>

          {err && <div className="border border-amber-300 bg-amber-50 rounded p-3 text-xs text-amber-800" data-testid="att-error">{err}</div>}

          {data && rows.length === 0 && (
            <div className="border border-slate-200 bg-slate-50/60 rounded py-10 text-center text-xs text-slate-400" data-testid="att-empty">
              No labour was assigned to this project on {date}.
            </div>
          )}

          {data && rows.length > 0 && (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <SummaryStrip list={marked} />
                {canRate && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-violet-900 bg-violet-50 border border-violet-300 rounded px-2.5 py-1" data-testid="att-projected-cost">
                    Projected Labour Cost: <span className="text-xs" data-testid="att-projected-total">{inr(projectedTotal)}</span>
                    <span className="font-normal text-violet-600">(units incl. OT × per-day ₹ — floats to Labour Payments)</span>
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs flex-wrap">
                <div className="relative">
                  <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="att-search" className="border border-slate-300 rounded pl-6 pr-2 py-1.5 text-xs w-56"
                    placeholder="Search labour name / tag / ID…"
                    value={q} onChange={(e) => setQ(e.target.value)} />
                </div>
                {qt && <span className="text-[11px] text-slate-500" data-testid="att-search-count">{visible.length} of {rows.length} shown</span>}
                {editable && (
                  <>
                    <button data-testid="att-mark-all-present" onClick={() => applyStatus("Present", visible)}
                      title={qt ? "Applies to the filtered list only" : "Applies to all rows"}
                      className="inline-flex items-center gap-1 px-2.5 py-1 font-bold text-emerald-800 border border-emerald-300 rounded hover:bg-emerald-50">
                      <CheckCircle2 size={12} /> Mark {qt ? "Filtered" : "All"} Present</button>
                    <button data-testid="att-mark-all-absent" onClick={() => applyStatus("Absent", visible)}
                      title={qt ? "Applies to the filtered list only" : "Applies to all rows"}
                      className="inline-flex items-center gap-1 px-2.5 py-1 font-bold text-orange-800 border border-orange-300 rounded hover:bg-orange-50">
                      <XCircle size={12} /> Mark {qt ? "Filtered" : "All"} Absent</button>
                    {tickedRows.length > 0 && (
                      <>
                        <span className="text-slate-300">|</span>
                        <button data-testid="att-mark-ticked-present" onClick={() => applyStatus("Present", tickedRows)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 font-bold text-white bg-emerald-700 rounded hover:bg-emerald-800">
                          <CheckCircle2 size={12} /> Mark Ticked Present ({tickedRows.length})</button>
                        <button data-testid="att-mark-ticked-absent" onClick={() => applyStatus("Absent", tickedRows)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 font-bold text-white bg-orange-600 rounded hover:bg-orange-700">
                          <XCircle size={12} /> Mark Ticked Absent ({tickedRows.length})</button>
                        <button data-testid="att-clear-ticks" onClick={() => setTicks({})}
                          className="text-slate-500 hover:text-slate-800 font-bold">Clear ticks</button>
                      </>
                    )}
                  </>
                )}
              </div>
              <div className="border border-slate-200 rounded overflow-x-auto">
                <table className="w-full text-xs" data-testid="att-rows-table">
                  <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                    <tr>
                      {editable && (
                        <th className="px-2 py-2">
                          <input type="checkbox" data-testid="att-tick-all"
                            checked={visible.length > 0 && visible.every((r) => ticks[r.labour_ref_id])}
                            onChange={toggleTickAll} title="Tick all shown rows" />
                        </th>
                      )}
                      <th className="px-3 py-2 text-left">Labour ID</th><th className="px-3 py-2 text-left">Name</th>
                      <th className="px-3 py-2 text-left">Tag</th>
                      <th className="px-3 py-2 text-left">Skill Category</th><th className="px-3 py-2 text-left">Labour Type</th>
                      <th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Overtime (qty)</th>
                      {canRate && <th className="px-3 py-2 text-right">Per Day ₹</th>}
                      {canRate && <th className="px-3 py-2 text-right">Est. ₹</th>}
                      <th className="px-3 py-2 text-left">Category 1</th><th className="px-3 py-2 text-left">Category 2</th>
                      <th className="px-3 py-2 text-left">Remark (optional)</th></tr>
                  </thead>
                  <tbody>
                    {rowPager.slice.map((r) => (
                      <tr key={r.labour_ref_id} className="border-t border-slate-100" data-testid={`att-row-${r.labour_code}`}>
                        {editable && (
                          <td className="px-2 py-2">
                            <input type="checkbox" data-testid={`att-tick-${r.labour_code}`}
                              checked={!!ticks[r.labour_ref_id]}
                              onChange={(e) => setTicks((t) => { const n = { ...t }; if (e.target.checked) n[r.labour_ref_id] = true; else delete n[r.labour_ref_id]; return n; })} />
                          </td>
                        )}
                        <td className="px-3 py-2 font-mono font-bold">{r.labour_code}</td>
                        <td className="px-3 py-2">{r.name}</td>
                        <td className="px-3 py-2" data-testid={`att-tag-${r.labour_code}`}>
                          {r.tag ? <span className="inline-block bg-indigo-50 border border-indigo-200 text-indigo-800 rounded px-1.5 py-0.5 text-[10px] font-bold">{r.tag}</span> : "—"}
                        </td>
                        <td className="px-3 py-2">{r.skill_category}</td>
                        <td className="px-3 py-2">{r.labour_type}</td>
                        <td className="px-3 py-2">
                          {editable ? (
                            <select data-testid={`att-status-${r.labour_code}`} className={`${inputCls} ${!(marks[r.labour_ref_id]?.status) ? "border-rose-400 text-rose-600" : ""}`}
                              value={marks[r.labour_ref_id]?.status || ""}
                              onChange={(e) => setMark(r.labour_ref_id, { status: e.target.value, ...(e.target.value !== "Present" ? { overtime: 0 } : {}) })}>
                              <option value="">— Not set —</option>
                              {STATUSES.map((st) => <option key={st}>{st}</option>)}
                            </select>
                          ) : <span className="font-bold">{marks[r.labour_ref_id]?.status || r.status}</span>}
                        </td>
                        <td className="px-3 py-2">
                          {editable ? (
                            <input data-testid={`att-overtime-${r.labour_code}`} type="number" min="0" step="any"
                              className={`${inputCls} w-20 ${(marks[r.labour_ref_id]?.status || "") !== "Present" ? "opacity-40" : ""}`}
                              placeholder="0" title="Extra units beyond the full day (e.g. 0.5, 1, 1.2) — full-day Present only"
                              disabled={(marks[r.labour_ref_id]?.status || "") !== "Present"}
                              value={marks[r.labour_ref_id]?.overtime || ""}
                              onChange={(e) => setMark(r.labour_ref_id, { overtime: e.target.value })} />
                          ) : ((marks[r.labour_ref_id]?.overtime || r.overtime) > 0
                            ? <span className="text-orange-700 font-bold">+{marks[r.labour_ref_id]?.overtime ?? r.overtime}</span> : "—")}
                        </td>
                        {canRate && (
                          <td className="px-3 py-2 text-right min-w-[8rem] whitespace-nowrap">
                            {canEditRate && editable && !wages[r.labour_ref_id]?.locked ? (
                              <input data-testid={`att-rate-${r.labour_code}`} type="number" min="0" step="any"
                                className={`${inputCls} w-28 min-w-[7rem] text-right ${parseFloat(rates[r.labour_ref_id]) !== (wages[r.labour_ref_id]?.per_day_amount ?? 0) ? "border-violet-400 bg-violet-50/50" : ""}`}
                                title={wages[r.labour_ref_id]?.rate_source === "override" ? "Day override already active (overrides the library rate)" : "From Labour Library — change to override for this day only"}
                                value={rates[r.labour_ref_id] ?? ""}
                                onChange={(e) => setRates((x) => ({ ...x, [r.labour_ref_id]: e.target.value }))} />
                            ) : (
                              <span className={`font-bold ${wages[r.labour_ref_id]?.locked ? "text-slate-400" : ""}`}
                                title={wages[r.labour_ref_id]?.locked ? `Locked — payment request ${wages[r.labour_ref_id]?.lock_request_number || ""}` : undefined}>
                                {inr(rateOf(r.labour_ref_id))}
                                {wages[r.labour_ref_id]?.locked && <Lock size={10} className="inline ml-1 -mt-0.5" />}
                              </span>
                            )}
                            {wages[r.labour_ref_id]?.rate_source === "override" && !wages[r.labour_ref_id]?.locked && (
                              <div className="text-[9px] text-violet-600 font-bold uppercase">override</div>
                            )}
                          </td>
                        )}
                        {canRate && (
                          <td className="px-3 py-2 text-right font-bold text-violet-900" data-testid={`att-est-${r.labour_code}`}>
                            {estOf(r) > 0 ? inr(estOf(r)) : "—"}
                          </td>
                        )}
                        <td className="px-3 py-2">
                          {editable ? (
                            <select data-testid={`att-cat1-${r.labour_code}`} className={`${inputCls} min-w-[110px]`}
                              value={marks[r.labour_ref_id]?.category1_id || ""}
                              onChange={(e) => setMark(r.labour_ref_id, { category1_id: e.target.value })}>
                              <option value="">—</option>
                              {cats.cat1.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          ) : (r.category1_name || "—")}
                        </td>
                        <td className="px-3 py-2">
                          {editable ? (
                            <select data-testid={`att-cat2-${r.labour_code}`} className={`${inputCls} min-w-[110px]`}
                              value={marks[r.labour_ref_id]?.category2_id || ""}
                              onChange={(e) => setMark(r.labour_ref_id, { category2_id: e.target.value })}>
                              <option value="">—</option>
                              {cats.cat2.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          ) : (r.category2_name || "—")}
                        </td>
                        <td className="px-3 py-2">
                          {editable ? (
                            <input data-testid={`att-remark-${r.labour_code}`} className={inputCls} placeholder="remark…"
                              value={marks[r.labour_ref_id]?.remark || ""}
                              onChange={(e) => setMark(r.labour_ref_id, { remark: e.target.value })} />
                          ) : (marks[r.labour_ref_id]?.remark || r.remark || "—")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Paginator pager={rowPager} testid="att-rows-pager" />
              </div>
              <SkillSummaryTable list={marked} />

              {editable ? (
                <div className="space-y-2">
                  {sheet && correcting && (
                    <label className="block space-y-1 text-xs"><span className="font-bold text-amber-700">Correction Reason *</span>
                      <input data-testid="att-reason-input" className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} /></label>
                  )}
                  <div className="flex items-center gap-3 flex-wrap text-xs" data-testid="att-attachment-block">
                    <span className="font-bold text-slate-600">Site Attendance Photo / PDF {sheet ? <span className="font-normal text-slate-400">(optional — replaces the existing one)</span> : "*"}</span>
                    <input data-testid="att-file-input" type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
                      onChange={(e) => setAttFile(e.target.files?.[0] || null)} className="text-[11px]" />
                    {attFile && <span className="text-[10px] text-slate-500">{attFile.name} · {(attFile.size / 1024).toFixed(0)} KB{attFile.size > 500 * 1024 ? " (auto-reduces to ≤500 KB)" : ""}</span>}
                    {sheet?.attachment?.filename && !attFile && (
                      <button type="button" data-testid="att-view-current" onClick={() => openSheetAttachment(project.id, sheet.id)}
                        className="inline-flex items-center gap-1 text-sky-700 hover:underline"><Paperclip size={11} /> {sheet.attachment.filename}</button>
                    )}
                  </div>
                  <label className="block space-y-1 text-xs"><span className="font-bold text-slate-600">Overall Site-Attendance Remark *</span>
                    <textarea data-testid="att-site-remark" className={`${inputCls} h-14`} placeholder="e.g. Full strength — slab casting at Block B"
                      value={siteRemark} onChange={(e) => setSiteRemark(e.target.value)} /></label>
                  <div className="flex justify-end gap-2">
                    {sheet && correcting && (
                      <button data-testid="att-cancel-correct" onClick={() => load()} className="px-3 py-1.5 border rounded text-xs">Cancel</button>
                    )}
                    <button data-testid="att-save-btn" disabled={busy} onClick={save}
                      className="px-4 py-1.5 bg-emerald-700 text-white text-xs font-bold rounded disabled:opacity-50">
                      {busy ? "Saving…" : sheet && correcting ? "Save Correction" : "Save Attendance"}
                    </button>
                  </div>
                </div>
              ) : sheet && (
                <div className="flex items-center justify-between gap-2 flex-wrap text-xs">
                  <div className="text-slate-500">Site remark: <i>{sheet.site_remark}</i> · by {sheet.created_by}</div>
                  {canEdit && (
                    <button data-testid="att-correct-btn" onClick={() => setCorrecting(true)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 font-bold text-amber-800 border border-amber-300 rounded hover:bg-amber-50">
                      <Pencil size={12} /> Correct Attendance</button>
                  )}
                </div>
              )}
              {(sheet?.revisions || []).length > 0 && !correcting && (
                <button data-testid="att-open-revisions" onClick={() => setDetail(sheet.id)}
                  className="text-[11px] text-sky-700 font-bold hover:underline">
                  View correction history ({sheet.revisions.length} revision{sheet.revisions.length > 1 ? "s" : ""})
                </button>
              )}
              <div className="text-[9px] text-slate-400">
                Present = 1.0 + overtime qty · Half Day = 0.5 · others = 0 (effective count feeds Labour Payments — no payroll or accounting effect) · Aadhaar never appears in attendance{canEditRate ? " · a changed Per Day ₹ becomes a day override that flows to the Labour Payments wage register" : ""}
              </div>
            </>
          )}
        </div>
      )}

      {view === "history" && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <input data-testid="att-hist-from" type="date" className="border border-slate-300 rounded px-2 py-1.5"
              value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
            <span className="text-slate-400">to</span>
            <input data-testid="att-hist-to" type="date" className="border border-slate-300 rounded px-2 py-1.5"
              value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
          </div>
          <div className="border border-slate-200 rounded overflow-x-auto">
            <table className="w-full text-xs" data-testid="att-hist-table">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-right">Assigned</th>
                  <th className="px-3 py-2 text-right">Present</th><th className="px-3 py-2 text-right">Half Day</th>
                  <th className="px-3 py-2 text-right">Effective</th><th className="px-3 py-2 text-left">Rev</th>
                  <th className="px-3 py-2 text-left">Marked By</th><th className="px-3 py-2"></th></tr>
              </thead>
              <tbody>
                {(hist.rows || []).length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400" data-testid="att-hist-empty">No attendance sheets in this range.</td></tr>
                )}
                {histPager.slice.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`att-hist-${s.attendance_date}`}>
                    <td className="px-3 py-2 font-mono font-bold">{s.attendance_date}</td>
                    <td className="px-3 py-2 text-right">{s.summary?.total_assigned}</td>
                    <td className="px-3 py-2 text-right">{s.summary?.present}</td>
                    <td className="px-3 py-2 text-right">{s.summary?.half_day}</td>
                    <td className="px-3 py-2 text-right font-bold text-emerald-800">{s.summary?.effective_count}</td>
                    <td className="px-3 py-2">Rev {s.revision}{s.backdated_entry ? <span className="ml-1 text-[9px] text-amber-700 font-bold uppercase">backdated</span> : ""}</td>
                    <td className="px-3 py-2">{s.created_by}</td>
                    <td className="px-3 py-2 text-right">
                      <button data-testid={`att-hist-view-${s.attendance_date}`} onClick={() => setDetail(s.id)}
                        className="text-sky-700 hover:underline inline-flex items-center gap-1"><Eye size={12} /> View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginator pager={histPager} testid="att-hist-pager" />
          </div>
        </div>
      )}

      {detail && <SheetDetailModal project={project} sheetId={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
